import { useState, useMemo } from 'react';
import { format, isToday, isFuture, isPast } from 'date-fns';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { TodoItemRow } from './TodoItemRow';
import type { TodoItem } from '@/lib/types';

interface CalendarViewProps {
  todos: TodoItem[];
  isLoading: boolean;
  onEdit: (todo: TodoItem) => void;
}

export function CalendarView({ todos, isLoading, onEdit }: CalendarViewProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  /** Dates that have at least one todo. */
  const todoDates = useMemo(() => {
    const set = new Set<string>();
    for (const todo of todos) {
      set.add(todo.date);
    }
    return set;
  }, [todos]);

  /** Todos for the currently selected date. */
  const selectedTodos = useMemo(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return todos.filter((t) => t.date === dateStr);
  }, [todos, selectedDate]);

  /** Upcoming todos (future, incomplete). */
  const upcomingTodos = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return todos
      .filter((t) => t.date > today && !t.completed)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 5);
  }, [todos]);

  /** Overdue todos (past, incomplete). */
  const overdueTodos = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return todos
      .filter((t) => t.date < today && !t.completed)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [todos]);

  const selectedDateLabel = format(selectedDate, 'EEEE, MMMM do');
  const selectedDateRelative = isToday(selectedDate)
    ? '(Today)'
    : isFuture(selectedDate)
      ? ''
      : isPast(selectedDate)
        ? '(Past)'
        : '';

  return (
    <div className="flex flex-col gap-6">
      {/* Calendar picker */}
      <Card className="border-border/40 shadow-md shadow-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="size-3.5 text-primary" />
            Encrypted Calendar
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            modifiers={{
              hasTodo: (date: Date) => todoDates.has(format(date, 'yyyy-MM-dd')),
            }}
            modifiersClassNames={{
              hasTodo: 'font-bold text-primary',
            }}
          />
        </CardContent>
      </Card>

      {/* Selected date todos */}
      <Card className="border-border/40 shadow-md shadow-primary/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">{selectedDateLabel}</CardTitle>
              {selectedDateRelative && (
                <CardDescription className="text-primary text-xs">{selectedDateRelative}</CardDescription>
              )}
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => {
                  const prev = new Date(selectedDate);
                  prev.setDate(prev.getDate() - 1);
                  setSelectedDate(prev);
                }}
                aria-label="Previous day"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => {
                  const next = new Date(selectedDate);
                  next.setDate(next.getDate() + 1);
                  setSelectedDate(next);
                }}
                aria-label="Next day"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-14 w-full rounded-lg" />
              <Skeleton className="h-14 w-full rounded-lg" />
            </div>
          ) : selectedTodos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border/60 py-8 px-6 text-center">
              <p className="text-muted-foreground text-sm">No items for this date.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {selectedTodos.map((todo) => (
                <TodoItemRow key={todo.id} todo={todo} onEdit={onEdit} compact />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overdue */}
      {overdueTodos.length > 0 && (
        <Card className="border-destructive/20 shadow-md">
          <CardHeader>
            <CardTitle className="text-base text-destructive">
              Overdue ({overdueTodos.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {overdueTodos.map((todo) => (
                <TodoItemRow key={todo.id} todo={todo} onEdit={onEdit} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming */}
      {upcomingTodos.length > 0 && (
        <Card className="border-border/40 shadow-md shadow-primary/5">
          <CardHeader>
            <CardTitle className="text-base">Upcoming</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {upcomingTodos.map((todo) => (
                <TodoItemRow key={todo.id} todo={todo} onEdit={onEdit} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
